// logger.js - Console log interceptor for error reporting
// Intercepts console.log/warn/error and buffers recent entries in memory

const MAX_LOGS = 200;
const MAX_ERRORS = 20;

let logs = [];
let recentErrors = [];
let initialized = false;

// Original console methods
const originalConsole = {
  log: console.log.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
  info: console.info.bind(console),
  debug: console.debug.bind(console),
};

/**
 * Format arguments to string for logging
 */
function formatArgs(args) {
  return args.map(arg => {
    if (arg === null) return 'null';
    if (arg === undefined) return 'undefined';
    if (typeof arg === 'object') {
      try {
        // Handle Error objects specially
        if (arg instanceof Error) {
          return `${arg.name}: ${arg.message}${arg.stack ? '\n' + arg.stack : ''}`;
        }
        return JSON.stringify(arg, null, 0);
      } catch {
        return String(arg);
      }
    }
    return String(arg);
  }).join(' ');
}

/**
 * Add a log entry to the buffer
 */
function addLog(level, args) {
  const entry = {
    timestamp: new Date().toISOString(),
    level,
    message: formatArgs(args),
  };

  logs.push(entry);

  // Trim to max size
  if (logs.length > MAX_LOGS) {
    logs = logs.slice(-MAX_LOGS);
  }

  // Track errors separately
  if (level === 'error') {
    recentErrors.push(entry);
    if (recentErrors.length > MAX_ERRORS) {
      recentErrors = recentErrors.slice(-MAX_ERRORS);
    }
  }
}

/**
 * Initialize the logger - intercepts console methods
 * Call this once at app startup
 */
export function initLogger() {
  if (initialized) return;
  initialized = true;

  // Add initial log entry
  addLog('info', ['[Logger] Initialized']);

  // Intercept console methods
  console.log = (...args) => {
    addLog('log', args);
    originalConsole.log(...args);
  };

  console.warn = (...args) => {
    addLog('warn', args);
    originalConsole.warn(...args);
  };

  console.error = (...args) => {
    addLog('error', args);
    originalConsole.error(...args);
  };

  console.info = (...args) => {
    addLog('info', args);
    originalConsole.info(...args);
  };

  console.debug = (...args) => {
    addLog('debug', args);
    originalConsole.debug(...args);
  };

  // Capture unhandled errors
  window.addEventListener('error', (event) => {
    addLog('error', [`[Unhandled] ${event.message} at ${event.filename}:${event.lineno}:${event.colno}`]);
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    if (reason instanceof Error) {
      addLog('error', [`[UnhandledRejection] ${reason.name}: ${reason.message}`]);
    } else {
      addLog('error', [`[UnhandledRejection] ${String(reason)}`]);
    }
  });
}

/**
 * Get all buffered logs (up to MAX_LOGS entries)
 */
export function getLogs() {
  return [...logs];
}

/**
 * Get recent error logs (up to MAX_ERRORS entries)
 */
export function getRecentErrors() {
  return [...recentErrors];
}

/**
 * Clear all logs (useful after successful report submission)
 */
export function clearLogs() {
  logs = [];
  recentErrors = [];
}
